import { parseSmartyStreetsResponse } from './parse-smartystreets-response';
import { pick, get, isEqual } from 'lodash-es';
import {
  ADDRESS_FIELDS,
  ADDRESS_STATUS,
  ADDRESS_CLASSIFICATION,
  REQUIRED_FIELDS,
} from '../constants';

const { assign, keys } = Object;

const TO_SMARTYSTREETS_MAP = {
  [ADDRESS_FIELDS.BASE_STREET]: 'street',
  [ADDRESS_FIELDS.APT]: 'secondary',
  [ADDRESS_FIELDS.CITY]: 'city',
  [ADDRESS_FIELDS.STATE]: 'state',
  [ADDRESS_FIELDS.ZIP]: 'zipcode',
};

const SAMPLE_FIELDS_1 = [
  ADDRESS_FIELDS.BASE_STREET,
  ADDRESS_FIELDS.APT,
  ADDRESS_FIELDS.CITY,
  ADDRESS_FIELDS.STATE,
];
const SAMPLE_FIELDS_2 = [
  ADDRESS_FIELDS.BASE_STREET,
  ADDRESS_FIELDS.APT,
  ADDRESS_FIELDS.ZIP,
];

export const INVALID_RESPONSE = {
  status: ADDRESS_STATUS.INVALID,
  classification: ADDRESS_CLASSIFICATION.UNKNOWN,
  analysis: null,
  components: null,
  metadata: null,
};

export const ENDPOINT = 'https://api.smartystreets.com/street-address';

export class SmartyStreetsService {
  constructor($q, $http, updEnvironment, $sce) {
    'ngInject';

    // `authToken` is the only key that is always required. For testing scenarios (e2e) we
    // may also have the `authId`.
    if (!get(updEnvironment, 'keys.smartystreets.authToken')) {
      throw new Error(
        '[SmartyStreetsService] updEnvironment does not contain smartystreets `authToken` key'
      );
    }

    assign(this, {
      $q,
      $http,
      $sce,
      keys: updEnvironment.keys.smartystreets,
    });
  }

  _buildHttpCalls(addresses) {
    return addresses
      .map(mapToSmartyStreets)
      .map(address => assign({}, this._params, address))
      .map(params =>
        this.$http.jsonp(this.$sce.trustAsResourceUrl(ENDPOINT), { params })
      );
  }

  get _params() {
    return {
      'auth-id': this.keys.authId,
      'auth-token': this.keys.authToken,
      jsonpCallbackParam: 'cb',
    };
  }

  request(address) {
    // Need to get 2 samples so we can compare the responses from
    // Smartystreets so we know whether to update the address or not
    const addresses = address ? getSamples(address) : [];
    const calls = this._buildHttpCalls(addresses);

    // Would love to do this in a single call using
    // smartystreets POST method, but for client side
    // auth we can't! :-(
    // https://smartystreets.com/docs/cloud/us-street-api#http-request-methods
    return this.$q.all(calls).then(success, error);

    function success(responses = []) {
      const results = responses
        .map(response => response.data[0])
        .map(data => processResponse(data))
        .filter(response => keys(response).length);

      // Need to compare the two samples results to determine if we
      // have a valid address. If not we don't know the validity
      // of the address, so mark it as invalid and unknown.
      return areResultsEqual(results) ? results[0] : INVALID_RESPONSE;
    }

    function error() {
      return INVALID_RESPONSE;
    }
  }
}

export function getSamples(address) {
  return [pick(address, SAMPLE_FIELDS_1), pick(address, SAMPLE_FIELDS_2)];
}

function processResponse(response) {
  return response ? parseSmartyStreetsResponse(response) : {};
}

export function mapToSmartyStreets(address) {
  const map = TO_SMARTYSTREETS_MAP;

  return Object.keys(map).reduce(
    (result, key) =>
      assign(result, {
        [map[key]]: address[key],
      }),
    {}
  );
}

function areResultsEqual(results) {
  const [address1, address2] = results.map(result =>
    pick(result, REQUIRED_FIELDS)
  );
  return results.length && isEqual(address1, address2);
}
